import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { trackTableDeleted, trackTableRenamed } from '@/analytics/utils/table-analytics'
import { TableTitlesContext } from '../components/table-titles-context'
import { useContext, useRef, useState } from 'react'
import { deleteTable } from '../fetch/delete-table'
import { postEditTable } from '../fetch/post-edit-table'
import type { TableEdit } from '../schemas'

export function useTableActions(tableId: string, onChanged: () => void, onDeleted: () => void) {
  const titles = useContext(TableTitlesContext)
  const { trackEvent } = useAnalytics()
  const [action, setAction] = useState<'rename' | 'delete' | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inFlight = useRef(false)
  const submit = async (edit?: TableEdit) => {
    if (inFlight.current) return false
    inFlight.current = true
    setSaving(true)
    setError(null)
    try {
      if (edit) {
        await postEditTable(tableId, edit)
        if (edit.action === 'rename-table') trackTableRenamed(trackEvent, tableId)
      } else {
        await deleteTable(tableId)
        trackTableDeleted(trackEvent, tableId)
      }
      setAction(null)
      if (!edit) onDeleted()
      return true
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to update table.')
      return false
    } finally {
      inFlight.current = false
      setSaving(false)
      titles?.refresh()
      onChanged()
    }
  }
  const choose = (next: 'rename' | 'delete') => {
    setError(null)
    setAction(next)
  }
  return { action, saving, error, submit, choose, close: () => setAction(null) }
}
